/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { LanguageModel } from '@repo/common/types/Download';
import { getTranscribeModels } from '@repo/common/types/Download';
import { derived, get, writable, type Readable } from 'svelte/store';

import type { SupportedModels } from '@repo/common/types/Exchange';
import { debounceAsync } from '@repo/toolslib/src/utils/debounce.util';
import { getHandler } from '../sharedlib';
import { DEFAULT_MODEL } from '../utils/aiModels';
import constants from '../utils/constants';
import { loadDurableStore } from '../utils/store.util';
import { userApiToken, userApiTokenM } from './auth.store';

// Type guard to check if a string is a valid SupportedModels
function isValidSupportedModel(model: string): model is SupportedModels {
  const supportedModels: SupportedModels[] = [
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-5.2',
    'gpt-5.2-mini',
    'gpt-5.2-nano',
    'gpt-5',
    'gpt-5-mini',
    'gpt-5-nano',
    'gpt-4.1-nano',
    'gpt-4.1-mini',
    'gpt-4.1',
    'claude-3-opus-latest',
    'claude-3-5-sonnet-latest',
    'claude-sonnet-4-20250514',
    'claude-sonnet-4-5-20250929',
    'claude-haiku-4-5-20251001',
    'gemini-1.5-pro',
    'gemini-1.5-flash',
    'gemini-2.5-flash',
    'gemini-2.5-pro',
    'gemini-3-flash-preview',
    'gemini-3-pro-preview',
    'meta-llama/llama-4-scout-17b-16e-instruct',
    'openai/gpt-oss-120b',
    'openai/gpt-oss-20b'
  ];
  return supportedModels.includes(model as SupportedModels);
}

type Config = {
  apiEndpoint: string;
  apiKey: string;
  gptModel: SupportedModels;
  notionKey: string;
  loaded: boolean;
  modalOpen: boolean;
  userName: string;
  notionDBId: string;
  enabledCalendars: string[];
  preferredMicrophones: string[];
  useDatabase: boolean;
  lastUsedDevice: string | null;
  rtTranscribeModel: string;
  finalTranscribeModel: string;
  lastSanitized?: Date;
  lastReindexed?: Date;
  lastCloudSyncAt?: Date;
  darkMode: 'auto' | 'light' | 'dark';
  dismissedUpdateVersions?: Record<string, number>; // version -> dismissal timestamp
};

export const config = writable<Config>({
  apiEndpoint: '',
  apiKey: '',
  gptModel: DEFAULT_MODEL,
  notionKey: '',
  notionDBId: '',
  userName: '',
  enabledCalendars: [],
  loaded: false,
  modalOpen: false,
  preferredMicrophones: [],
  useDatabase: false,
  lastUsedDevice: '',
  rtTranscribeModel: '',
  finalTranscribeModel: '',
  lastSanitized: undefined,
  lastReindexed: undefined,
  lastCloudSyncAt: undefined,
  darkMode: 'auto',
  dismissedUpdateVersions: {}
});

const filename = '.settings.dat';

const persistConfig = async (c: Config) => {
  if (!c.loaded) {
    return false;
  }
  const store = await loadDurableStore(filename);
  // Coerce undefined values to safe defaults to satisfy Tauri store's strict arg checks
  await store.set('api-endpoint', c.apiEndpoint ?? '');
  await store.set('api-key', c.apiKey ?? '');
  await store.set('gpt-model', c.gptModel ?? DEFAULT_MODEL);
  await store.set('notion-key', c.notionKey ?? '');
  await store.set('notion-db-id', c.notionDBId ?? '');
  await store.set('user-name', c.userName ?? '');
  await store.set('enabledCalendars', c.enabledCalendars ?? []);
  await store.set('preferredMicrophones', c.preferredMicrophones ?? []);
  await store.set('useDatabase', c.useDatabase ?? false);
  await store.set('lastUsedDevice', c.lastUsedDevice ?? '');
  await store.set('rtTranscribeModel', c.rtTranscribeModel ?? '');
  await store.set('finalTranscribeModel', c.finalTranscribeModel ?? '');
  await store.set('lastSanitized', c.lastSanitized ? c.lastSanitized.toISOString() : '');
  await store.set('lastReindexed', c.lastReindexed ? c.lastReindexed.toISOString() : '');
  await store.set('darkMode', c.darkMode ?? 'auto');
  await store.set('dismissedUpdateVersions', c.dismissedUpdateVersions ?? {});
  await store.save();
};

config.subscribe(debounceAsync(persistConfig, 'config.subscribe', 1500));

export async function loadConfig() {
  const store = await loadDurableStore(filename, true);
  const apiEndpoint: string = (await store.get('api-endpoint')) || constants.API_SCHEME_HOST;
  const apiKey: string = (await store.get('api-key')) || '';
  const gptModelFromStorage: string = (await store.get('gpt-model')) || DEFAULT_MODEL;
  const gptModel: SupportedModels = isValidSupportedModel(gptModelFromStorage)
    ? gptModelFromStorage
    : DEFAULT_MODEL;
  const notionKey: string = (await store.get('notion-key')) || '';
  const notionDBId: string = (await store.get('notion-db-id')) || '';
  const userName: string = (await store.get('user-name')) || 'You';
  const enabledCalendars: string[] = (await store.get('enabledCalendars')) || [];
  const preferredMicrophones: string[] = (await store.get('preferredMicrophones')) || [];
  const useDatabase: boolean = (await store.get('useDatabase')) || false;
  const lastUsedDevice: string = (await store.get('lastUsedDevice')) || '';
  const storedRtModel: string = (await store.get('rtTranscribeModel')) || '';
  const storedFinalModel: string = (await store.get('finalTranscribeModel')) || '';

  // Ensure we always have valid model selections - NEVER allow empty strings

  const models = getTranscribeModels();

  const defaultModel = models.find((model) => model.id === 'ggml-large-v3-turbo-q8_0');
  if (!defaultModel) {
    throw new Error('Default model not found');
  }

  const defaultRtModel = models.find((model) => model.id === 'ggml-small-q5_1') || defaultModel;
  const selectedRtModel =
    models.find((model) => model.id === storedRtModel.trim()) || defaultRtModel;

  const selectedFinalModel =
    models.find((model) => model.id === storedFinalModel.trim()) || defaultModel;

  const lastSanitizedStr: string = (await store.get('lastSanitized')) || '';
  const lastSanitized: Date | undefined = lastSanitizedStr ? new Date(lastSanitizedStr) : undefined;
  const lastReindexedStr: string = (await store.get('lastReindexed')) || '';
  const lastReindexed: Date | undefined = lastReindexedStr ? new Date(lastReindexedStr) : undefined;
  const darkMode: 'auto' | 'light' | 'dark' = (await store.get('darkMode')) || 'auto';
  const dismissedUpdateVersions: Record<string, number> = (await store.get('dismissedUpdateVersions')) || {};

  config.update((c) => {
    c.apiEndpoint = apiEndpoint;
    c.apiKey = apiKey;
    c.notionKey = notionKey;
    c.notionDBId = notionDBId;
    c.gptModel = gptModel;
    c.loaded = true;
    c.userName = userName;
    c.enabledCalendars = enabledCalendars;
    c.preferredMicrophones = preferredMicrophones;
    c.useDatabase = useDatabase;
    c.lastUsedDevice = lastUsedDevice;
    c.rtTranscribeModel = selectedRtModel.id;
    c.finalTranscribeModel = selectedFinalModel.id;
    c.lastSanitized = lastSanitized;
    c.lastReindexed = lastReindexed;
    c.darkMode = darkMode;
    c.dismissedUpdateVersions = dismissedUpdateVersions;
    return c;
  });

  // Load devices using centralized store function (do not await on purpose)
  import('./devices').then(({ loadDevices }) => {
    loadDevices(lastUsedDevice).catch((error) => {
      console.warn('Failed to load devices during config init:', error);
    });
  });

  // Initialize transcribe options with download status
  recalculateOptions().then();
}

export const getConfig = () => {
  const apiKey = get(userApiToken);
  const apiKeyM = get(userApiTokenM);
  const $config = get(config);
  return {
    apiKey,
    apiKeyM,
    gptModel: $config.gptModel || DEFAULT_MODEL
  };
};

export const getNotionConfig = () => {
  const $config = get(config);
  return {
    notionKey: $config.notionKey,
    notionDBId: $config.notionDBId
  };
};

export const apiEndpointStore: Readable<string> = derived(config, ($config) => {
  // If we're on the web, connect to the same web server
  return $config.apiEndpoint || constants.API_SCHEME_HOST;
});

export const getApiEndpoint = () => {
  const storedEndpoint = get(apiEndpointStore);

  // User-configured endpoint via deep link ALWAYS takes precedence
  if (storedEndpoint && storedEndpoint !== constants.API_SCHEME_HOST) {
    return storedEndpoint;
  }

  // Vercel preview URL for web builds
  if (constants.VITE_VERCEL_URL) {
    const scheme =
      constants.VITE_VERCEL_URL.includes('localhost') || constants.VITE_VERCEL_URL.includes('192')
        ? 'http'
        : 'https';
    return `${scheme}://${constants.VITE_VERCEL_URL}`;
  }

  // Fall back to stored endpoint or build-time default
  return storedEndpoint || constants.API_SCHEME_HOST;
};

// Transcribe options functionality migrated from onboarding.store
const defaultTranscribeOptions = getTranscribeModels();

export const transcribeOptions = writable<LanguageModel[]>(defaultTranscribeOptions);

export const recalculateOptions = async () => {
  const $transcribeOptions = get(transcribeOptions);

  const options = await Promise.all(
    $transcribeOptions.map(async (option) => {
      option.downloaded = await getHandler().modelIsDownloaded(option);
      return option;
    })
  );
  transcribeOptions.set(options);
};

export const selectedTranscribeOptions = derived(
  [transcribeOptions, config],
  ([$transcribeOptions, $config]) => {
    if (!$config.loaded) {
      return {
        loaded: false,
        rtTranscribeModel: null,
        finalTranscribeModel: null
      };
    }
    const defaultModel = $transcribeOptions.find(
      (option) => option.id === 'ggml-large-v3-turbo-q8_0'
    );
    const defaultRtModel =
      $transcribeOptions.find((option) => option.id === 'ggml-small-q5_1') || defaultModel;
    const rtTranscribeModel = $transcribeOptions.find(
      (option) => option.id === $config.rtTranscribeModel
    );
    const finalTranscribeModel = $transcribeOptions.find(
      (option) => option.id === $config.finalTranscribeModel
    );
    return {
      loaded: !!rtTranscribeModel && !!finalTranscribeModel,
      rtTranscribeModel: rtTranscribeModel || defaultRtModel,
      finalTranscribeModel: finalTranscribeModel || defaultModel
    };
  }
);
